import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input, InputNumber, Select, Space, Switch, Typography } from 'antd';
import { setSelectedStep, setWorkflow } from '../../../redux/slices/connectorSlice';
import { getPreviousFormsForConditions } from '../../../utils/workflowHelper';
import { getAllWidgets } from '../../../utils/formsHelper';
import SvgIcons from '../../layout/assets/SvgIcons';
import { trimStringValues } from '../../../utils/trimStrings';
import { useTranslation } from 'react-i18next';

const DEFAULT_EVENT_INFO = {
  extractFromTable: true,
  dataTable: '',
  meetingDate: '',
  meetingTime: '',
  meetingDuration: 60,
  meetingZone: 'Asia/Kolkata',
  guestEmails: [],
  meetingTitle: '',
};

const toArrayValue = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [];
};

const hiddenWidgetTypes = ['imageForm', 'infoForm', 'linkField', 'switchForm', 'divider', 'section', 'attachment'];

const timezoneOptions = [
  { label: 'Asia/Kolkata', value: 'Asia/Kolkata' },
  { label: 'UTC', value: 'UTC' },
  { label: 'Asia/Dubai', value: 'Asia/Dubai' },
  { label: 'Europe/London', value: 'Europe/London' },
  { label: 'America/New_York', value: 'America/New_York' },
  { label: 'America/Los_Angeles', value: 'America/Los_Angeles' },
];

const resolveColumnType = (column = {}) => {
  const rawType = String(column.inputType || column.formType || column.type || '').toLowerCase();
  if (rawType.includes('date')) return 'dateForm';
  if (rawType.includes('time')) return 'timeForm';
  if (rawType.includes('number')) return 'numberForm';
  if (rawType.includes('contact')) return 'contactField';
  if (rawType.includes('user')) return 'userForm';
  if (rawType.includes('select') || rawType.includes('dropdown')) return 'dropdownField';
  return 'inputForm';
};

const collectTableWidgets = (items = [], acc = []) => {
  items.forEach(item => {
    if (!item) return;
    if (item.formType === 'tableForm') {
      acc.push(item);
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      collectTableWidgets(item.children, acc);
    }
  });
  return acc;
};

const OutlookIntegrationPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedStep, workflow, workflowForms } = useSelector(s => s.connector);
  const [form] = Form.useForm();
  const [formItems, setFormItems] = useState([]);
  const [tableFields, setTableFields] = useState([]);
  const [tableColumnsByTableId, setTableColumnsByTableId] = useState({});
  const extractFromTable = Form.useWatch('extractFromTable', form);
  const selectedDataTable = Form.useWatch('dataTable', form);

  const isOutlookStep = !!(selectedStep?.id && (selectedStep?.data?.type === 'outlookCalendar' || selectedStep?.type === 'outlookCalendar'));
  const nodeId = selectedStep?.id;

  useEffect(() => {
    const el = document.getElementById('outlook-integration-panel');
    if (el) el.style.display = isOutlookStep ? 'block' : 'none';
  }, [isOutlookStep]);
  const eventInfo = !isOutlookStep ? DEFAULT_EVENT_INFO : { ...DEFAULT_EVENT_INFO, ...(selectedStep?.data?.outlookEventInfo || {}) };


  useEffect(() => {
    if (!isOutlookStep || !nodeId) return;
    const node = (workflow.nodes || []).find(n => n.id === nodeId);
    const nodeData = node?.data || {};
    // const guestEmailIds = eventInfo.guestEmails ? eventInfo.guestEmails.split(',').map(item => item.trim()).filter(Boolean) : [];
    form.setFieldsValue({
      name: nodeData?.name || 'Outlook Calendar',
      extractFromTable: eventInfo.extractFromTable,
      dataTable: eventInfo.dataTable,
      meetingDate: eventInfo.meetingDate,
      meetingTime: eventInfo.meetingTime,
      meetingDuration: eventInfo.meetingDuration,
      meetingZone: eventInfo.meetingZone,
      guestEmails: toArrayValue(eventInfo.guestEmails),
      meetingTitle: eventInfo.meetingTitle,
      additionalGuests: toArrayValue(eventInfo.additionalGuests),
    });
  }, [isOutlookStep, nodeId, workflow.nodes, form]);

  useEffect(() => {
    if (workflowForms?.length > 0 && workflow.nodes && isOutlookStep) {
      const previousNodes = getPreviousFormsForConditions(workflow.nodes, workflow.edges, selectedStep);
      const previousFormIds = new Set();
      const finalForms = [];

      previousNodes.forEach(node => {
        if (node?.formData?.formId && !previousFormIds.has(node.formData.formId)) {
          const nodeForm = workflowForms.find(formData => formData.formId === node.formData.formId);
          if (nodeForm) {
            previousFormIds.add(node.formData.formId);
            finalForms.push(nodeForm);
          }
        }
      });

      let elements = [];
      let tableWidgets = [];
      finalForms.forEach(curForm => {
        const allWidgets = getAllWidgets(curForm.items || []);
        elements = elements.concat(allWidgets);
        tableWidgets = tableWidgets.concat(collectTableWidgets(curForm.items || []));
      });

      elements = elements
        .filter(el => !hiddenWidgetTypes.includes(el.formType))
        .map(element => ({
          label: element.label,
          value: element.id,
          formType: element.formType,
          parentTableId: element.parentTableId,
        }));

      setFormItems(elements);

      const uniqueTableMap = new Map();
      tableWidgets.forEach(tbl => {
        if (tbl?.id && !uniqueTableMap.has(tbl.id)) {
          uniqueTableMap.set(tbl.id, { label: tbl.label || 'Table', value: tbl.id });
        }
      });
      setTableFields(Array.from(uniqueTableMap.values()));

      const tableColsMap = {};
      tableWidgets.forEach(tbl => {
        if (!tbl?.id) return;
        const cols = Array.isArray(tbl.columns) ? tbl.columns : [];
        tableColsMap[tbl.id] = cols.map(col => ({
          id: `${tbl.id}::${col.id}`,
          label: col.label || 'Column',
          formType: resolveColumnType(col),
        }));
      });
      setTableColumnsByTableId(tableColsMap);
    }
  }, [workflowForms, isOutlookStep, workflow.edges, workflow.nodes, selectedStep]);

  const tableDateOptions = useMemo(() => {
    const cols = tableColumnsByTableId[selectedDataTable] || [];
    return cols.filter(item => item.formType === 'dateForm').map(item => ({ label: item.label, value: item.id }));
  }, [tableColumnsByTableId, selectedDataTable]);
  const tableTimeOptions = useMemo(() => {
    const cols = tableColumnsByTableId[selectedDataTable] || [];
    return cols.filter(item => item.formType === 'timeForm').map(item => ({ label: item.label, value: item.id }));
  }, [tableColumnsByTableId, selectedDataTable]);

  const normalDateOptions = useMemo(() => {
    return formItems
      .filter(item => item.formType === 'dateForm' && !item.parentTableId)
      .map(item => ({ label: item.label, value: item.value }));
  }, [formItems]);

  const normalTimeOptions = useMemo(() => {
    return formItems
      .filter(item => item.formType === 'timeForm' && !item.parentTableId)
      .map(item => ({ label: item.label, value: item.value }));
  }, [formItems]);

  const dateOptions = extractFromTable ? tableDateOptions : normalDateOptions;
  const timeOptions = extractFromTable ? tableTimeOptions : normalTimeOptions;

  const tableOptions = useMemo(() => tableFields, [tableFields]);
  const attendeeOptionsBase = useMemo(() => {
    return formItems.filter(item => {
      const formType = item.formType || '';
      const label = (item.label || '').toLowerCase();
      return ['inputForm', 'userForm', 'dropdownField'].includes(formType) || label.includes('email') || label.includes('mail');
    }).map(item => ({ label: item.label, value: item.value }));
  }, [formItems]);

  const tableAttendeeOptions = useMemo(() => {
    const cols = tableColumnsByTableId[selectedDataTable] || [];
    return cols
      .filter(col => {
        const label = String(col.label || '').toLowerCase();
        return ['inputForm', 'contactField', 'userForm', 'dropdownField'].includes(col.formType) || label.includes('email') || label.includes('mail');
      })
      .map(col => ({ label: col.label, value: col.id }));
  }, [tableColumnsByTableId, selectedDataTable]);

  const normalAttendeeOptions = useMemo(() => {
    return attendeeOptionsBase.filter(item => {
      const raw = formItems.find(f => f.value === item.value);
      return !raw?.parentTableId;
    });
  }, [attendeeOptionsBase, formItems]);

  const attendeeOptions = extractFromTable ? tableAttendeeOptions : normalAttendeeOptions;

  const onClose = () => dispatch(setSelectedStep({}));

  useEffect(() => {
    const currentDate = form.getFieldValue('meetingDate');
    const currentTime = form.getFieldValue('meetingTime');
    const currentGuest = form.getFieldValue('guestEmails');

    if (dateOptions.length > 0 && currentDate && !dateOptions.some(opt => opt.value === currentDate)) {
      form.setFieldValue('meetingDate', undefined);
    }
    if (timeOptions.length > 0 && currentTime && !timeOptions.some(opt => opt.value === currentTime)) {
      form.setFieldValue('meetingTime', undefined);
    }
    if (attendeeOptions.length > 0 && Array.isArray(currentGuest) && currentGuest.length > 0) {
      const validValues = currentGuest.filter(val => attendeeOptions.some(opt => opt.value === val));
      if (validValues.length !== currentGuest.length) {
        form.setFieldValue('guestEmails', validValues);
      }
    }
  }, [extractFromTable, selectedDataTable, dateOptions, timeOptions, attendeeOptions, form]);

  const onSave = async () => {
    const values = trimStringValues(await form.validateFields());
    const guestEmailsValue = toArrayValue(values.guestEmails);
    const additionalGuestsValue = toArrayValue(values.additionalGuests);

    const nextNodes = (workflow.nodes || []).map(node => {
      if (node.id !== nodeId) return { ...node };
      return {
        ...node,
        data: {
          ...node.data,
          type: 'outlookCalendar',
          name: values.name || 'Outlook Calendar',
          outlookEventInfo: {
            ...DEFAULT_EVENT_INFO,
            extractFromTable: !!values.extractFromTable,
            dataTable: values.dataTable || '',
            meetingDate: values.meetingDate || '',
            meetingTime: values.meetingTime || '',
            meetingDuration: values.meetingDuration,
            meetingZone: values.meetingZone || 'Asia/Kolkata',
            guestEmails: guestEmailsValue,
            additionalGuests: additionalGuestsValue,
            meetingTitle: values.meetingTitle || '',
          },
        },
      };
    });

    dispatch(setWorkflow({ ...workflow, nodes: nextNodes }));
    onClose();
  };

  if (!isOutlookStep) return null;

  return (
    <>
      <div className="step-details-container" id="outlook-integration-panel">
        <Flex className='step-details-header' align='center' justify='space-between'>
          <Typography.Title level={5} className='margin-0'>
            <Space>
              <SvgIcons type='outlookCalendar' />
              {t('Outlook Calendar')}
            </Space>
          </Typography.Title>
          <CloseOutlined className='icon-medium' onClick={onClose} />
        </Flex>

        <div className='step-details-body'>
          <Form form={form} layout='vertical' onFinish={onSave}>
            <Form.Item label={t('Name')} name='name' rules={[{ required: true, whitespace: true, message: 'Name is required' }]}>
              <Input disabled />
            </Form.Item>

            <div className='mb-3 text-sm text-gray-600'>{t('Map Outlook fields directly from workflow fields.')}</div>

            <Form.Item label={t('Extract From Table')} name='extractFromTable' valuePropName='checked'>
              <Switch />
            </Form.Item>

            <Form.Item shouldUpdate={(prev, cur) => prev.extractFromTable !== cur.extractFromTable} noStyle>
              {({ getFieldValue }) => getFieldValue('extractFromTable') ? (
                <Form.Item label={t('Data Table')} name='dataTable' rules={[{ required: true, message: 'Select table field' }]}>
                  <Select allowClear showSearch optionFilterProp='label' options={tableOptions} placeholder={t('Select table field')} />
                </Form.Item>
              ) : null}
            </Form.Item>

            <Form.Item label={t('Meeting Date')} name='meetingDate' rules={[{ required: true, message: 'Select date field' }]}>
              <Select allowClear showSearch optionFilterProp='label' options={dateOptions} placeholder={t('Select date field')} />
            </Form.Item>

            <Form.Item label={t('Meeting Time')} name='meetingTime' rules={[{ required: true, message: 'Select time field' }]}>
              <Select allowClear showSearch optionFilterProp='label' options={timeOptions} placeholder={t('Select time field')} />
            </Form.Item>

            <Form.Item label={t('Meeting Duration (Minutes)')} name='meetingDuration' rules={[{ required: true, message: 'Enter duration' }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label={t('Meeting Zone')} name='meetingZone' rules={[{ required: true, message: 'Select timezone' }]}>
              <Select showSearch optionFilterProp='label' options={timezoneOptions} />
            </Form.Item>

            <Form.Item label={t('Guest Emails')} name='guestEmails' rules={[{ required: true, message: 'Select one field' }]}>
              <Select
                mode="multiple"
                showSearch
                optionFilterProp='label'
                options={attendeeOptions}
                placeholder={t('Select email field')}
              />
            </Form.Item>

            <Form.Item label={t('Additional Guests')} name='additionalGuests'>
              <Select
                mode='multiple'
                showSearch
                optionFilterProp='label'
                options={normalAttendeeOptions}
                placeholder={t('Select email field')}
              />
            </Form.Item>

            <Form.Item label={t('Meeting Title')} name='meetingTitle' rules={[{ required: true, whitespace: true, message: 'Meeting title is required' }]}>
              <Input placeholder='Induction - {{candidate_name}}' />
            </Form.Item>

            <Form.Item className='step-details-action'>
              <Flex gap={12}>
                <Button type='primary' htmlType='submit' className='submit-button' style={{ width: '9.75rem' }}>{t('Save')}</Button>
                <Button onClick={onClose} style={{ width: '9.75rem' }}>{t('Cancel')}</Button>
              </Flex>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};

export default OutlookIntegrationPanel;
